import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '../css/App.css';
import '../css/index.css';
import { Toaster } from '@/components/ui/toaster';
import { getSentryEnvironment } from '@/lib/sentryEnvironment';
import { registerVitePreloadErrorRecovery } from '@/lib/vitePreloadError';
import { recoverFromInertiaBootFailure } from '@/lib/inertiaBootRecovery';

import { createInertiaApp } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createRoot } from 'react-dom/client';
import type { ComponentType } from 'react';
import { route as routeFn } from 'ziggy-js';
declare global {
    const route: typeof routeFn;
    interface Window {
        Sentry: typeof Sentry;
    }
}

registerVitePreloadErrorRecovery();

Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_REACT_DSN,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    environment: getSentryEnvironment(document),
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REACT_TRACES_SAMPLE_RATE) ?? 0.0,
    tracePropagationTargets: ['localhost', /^https:\/\/.*shespeakscyber\..*/],
    // Replays turned off for performance and privacy considerations, capture errors at 100%
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
});

// Expose Sentry globally for testing
window.Sentry = Sentry;

const queryClient = new QueryClient();

createInertiaApp({
    title: title => `${title ? title + ` – SheSpeaksCyber` : 'SheSpeaksCyber – Female Cybersecurity Speaker Directory'}`,
    resolve: (name) => resolvePageComponent<ComponentType<any>>(`./pages/${name}.tsx`, import.meta.glob<ComponentType<any>>('./pages/**/*.tsx')),
    setup({ el, App, props }) {
        if (!el) {
            return;
        }

        const root = createRoot(el);

        root.render(
            <QueryClientProvider client={queryClient}>
                <Toaster />
                <App {...props} />
            </QueryClientProvider>
        );

    },
    progress: {
        color: '#4B5563',
    },
}).catch((error: unknown) => {
    Sentry.captureException(error);

    let storage: Storage | null = null;

    try {
        storage = window.sessionStorage;
    } catch {
    }

    recoverFromInertiaBootFailure(error, storage, () => window.location.reload());
});

